import React, { useState, useEffect } from 'react';
import { crmApi } from '@/api';
import { Contact } from '@/types';
import { Modal, Field, Input, Select, Textarea, Button, Badge, EmptyState, Card, Skeleton } from '@/ui';

const TYPE_TONE: Record<string, 'brand' | 'info' | 'neutral'> = {
  client: 'brand',
  realtor: 'info',
  other: 'neutral',
};

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  type: 'client' | 'realtor' | 'other';
  company: string;
  notes: string;
}

const emptyForm: ContactFormData = {
  name: '',
  email: '',
  phone: '',
  type: 'client',
  company: '',
  notes: '',
};

const Contacts: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState<ContactFormData>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const response = await crmApi.getContacts();
      if (response.success && response.data) {
        setContacts(response.data);
      }
    } catch {
      console.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingContact(null);
    setFormData(emptyForm);
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      email: contact.email,
      phone: contact.phone || '',
      type: contact.type,
      company: contact.company || '',
      notes: contact.notes || '',
    });
    setError(null);
    setShowModal(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name || !formData.email) {
      setError('Name and email are required.');
      return;
    }

    setSubmitting(true);
    try {
      if (editingContact) {
        const response = await crmApi.updateContact(editingContact.id, formData);
        if (response.success && response.data) {
          setContacts(prev => prev.map(c => c.id === editingContact.id ? response.data! : c));
        }
      } else {
        const response = await crmApi.createContact(formData as Parameters<typeof crmApi.createContact>[0]);
        if (response.success && response.data) {
          setContacts(prev => [response.data!, ...prev]);
        }
      }
      setShowModal(false);
    } catch {
      setError(`Failed to ${editingContact ? 'update' : 'create'} contact.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await crmApi.deleteContact(id);
      setContacts(prev => prev.filter(c => c.id !== id));
      setDeleteConfirm(null);
    } catch {
      console.error('Failed to delete contact');
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-display text-display-sm text-ink">Contacts</h1>
        <Button variant="primary" onClick={openCreateModal} leadingIcon={<PlusIcon />}>
          Add Contact
        </Button>
      </div>

      {loading ? (
        <div className="bg-surface-1 border border-border-subtle rounded-xl shadow-card overflow-hidden">
          <div className="bg-surface-2 px-6 py-3 flex gap-6 border-b border-border-subtle">
            <Skeleton width="w-24" height="h-3" />
            <Skeleton width="w-36" height="h-3" />
            <Skeleton width="w-20" height="h-3" />
            <Skeleton width="w-14" height="h-3" />
          </div>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="px-6 py-4 flex items-center gap-6 border-b border-border-subtle last:border-0">
              <div className="space-y-1.5 w-36">
                <Skeleton width="w-full" height="h-4" />
                <Skeleton width="w-3/4" height="h-3" />
              </div>
              <Skeleton width="w-44" height="h-3" />
              <Skeleton width="w-24" height="h-3" />
              <Skeleton width="w-14" height="h-5" shape="pill" />
            </div>
          ))}
        </div>
      ) : contacts.length === 0 ? (
        <EmptyState
          title="No contacts yet"
          description="Add your first contact to start building your CRM."
          action={
            <Button variant="primary" onClick={openCreateModal} leadingIcon={<PlusIcon />}>
              Add Your First Contact
            </Button>
          }
        />
      ) : (
        <Card variant="elevated" unpadded className="overflow-hidden">
          <table className="min-w-full divide-y divide-border-subtle">
            <thead className="bg-surface-2">
              <tr>
                <th className="px-6 py-3 text-left text-overline text-ink-subtle">Name</th>
                <th className="px-6 py-3 text-left text-overline text-ink-subtle">Email</th>
                <th className="px-6 py-3 text-left text-overline text-ink-subtle hidden sm:table-cell">Phone</th>
                <th className="px-6 py-3 text-left text-overline text-ink-subtle">Type</th>
                <th className="px-6 py-3 text-right text-overline text-ink-subtle">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-surface-1 divide-y divide-border-subtle">
              {contacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-surface-2 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-body-sm font-medium text-ink">{contact.name}</div>
                      {contact.company && (
                        <div className="text-caption text-ink-subtle">{contact.company}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-muted">{contact.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-muted hidden sm:table-cell">
                    {contact.phone || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge tone={TYPE_TONE[contact.type] || 'neutral'} compact>
                      {contact.type}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-body-sm">
                    <button
                      onClick={() => openEditModal(contact)}
                      className="text-brand hover:text-brand/80 transition-colors mr-3"
                    >
                      Edit
                    </button>
                    {deleteConfirm === contact.id ? (
                      <>
                        <button
                          onClick={() => handleDelete(contact.id)}
                          className="text-sev-major hover:text-sev-critical-ink transition-colors mr-2"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-ink-subtle hover:text-ink-muted transition-colors"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(contact.id)}
                        className="text-sev-major hover:text-sev-critical-ink transition-colors"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingContact ? 'Edit Contact' : 'Add Contact'}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
              loading={submitting}
            >
              {editingContact ? 'Save Changes' : 'Add Contact'}
            </Button>
          </>
        }
      >
        {error && (
          <div className="mb-4 p-3 bg-sev-major-soft text-sev-major-ink border border-sev-major/20 rounded-md text-body-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name" htmlFor="contact-name">
              <Input id="contact-name" name="name" value={formData.name} onChange={handleChange} required />
            </Field>
            <Field label="Email" htmlFor="contact-email">
              <Input
                id="contact-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Phone" htmlFor="contact-phone" optional>
              <Input id="contact-phone" type="tel" name="phone" value={formData.phone} onChange={handleChange} />
            </Field>
            <Field label="Type" htmlFor="contact-type">
              <Select id="contact-type" name="type" value={formData.type} onChange={handleChange}>
                <option value="client">Client</option>
                <option value="realtor">Realtor</option>
                <option value="other">Other</option>
              </Select>
            </Field>
          </div>

          <Field label="Company" htmlFor="contact-company" optional>
            <Input id="contact-company" name="company" value={formData.company} onChange={handleChange} />
          </Field>

          <Field label="Notes" htmlFor="contact-notes" optional>
            <Textarea id="contact-notes" name="notes" value={formData.notes} onChange={handleChange} rows={3} />
          </Field>

          <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
        </form>
      </Modal>
    </div>
  );
};

const PlusIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export default Contacts;
